import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const UfficientLandingHero = () => {
  const scrollToContent = () => {
    const headerHeight = document.querySelector('.main-layout-header')?.offsetHeight || 0;
    window.scrollTo({
      top: window.innerHeight - headerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-100 min-h-[65vh] sm:min-h-[85vh] md:min-h-[95vh] flex items-center pt-12 sm:pt-24 pb-8 sm:pb-16 overflow-hidden">
      {/* Background - Video for desktop, gradient for mobile */}
      <div className="absolute inset-0 w-full h-full">
        <div className="absolute inset-0 bg-black opacity-70 z-10"></div>
        
        {/* Mobile Background - Video (upscaled to full height) */}
        <div className="absolute inset-0 w-full h-full md:hidden">
          <iframe
            className="absolute w-full h-full"
            src="https://www.youtube.com/embed/aNcapNe1AVA?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playlist=aNcapNe1AVA&start=0&end=0&hd=1&quality=hd1080"
            title="Ufficient Background Video Mobile"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            style={{
              pointerEvents: 'none',
              opacity: 0.6,
              transform: 'scale(2.5)',
              transformOrigin: 'center center',
              minWidth: '100%',
              minHeight: '100%',
              width: 'auto',
              height: 'auto'
            }}
          ></iframe>
        </div>
        
        {/* Desktop Background - Video */}
        <div className="hidden md:block absolute inset-0 w-full h-full">
          <iframe
            className="absolute w-full h-full"
            src="https://www.youtube.com/embed/aNcapNe1AVA?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playlist=aNcapNe1AVA&start=0&end=0&hd=1&quality=hd1080"
            title="Ufficient Background Video"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            style={{
              pointerEvents: 'none',
              opacity: 0.6,
              transform: 'scale(1.3)',
              transformOrigin: 'center center',
              minWidth: '100%',
              minHeight: '100%',
              width: 'auto',
              height: 'auto'
            }}
          ></iframe>
        </div>
      </div>

      {/* Content - Now full width and centered */}
      <div className="relative z-20 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Vignette overlay behind text */}
        <div 
          className="absolute -inset-20 rounded-3xl transform scale-125"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, transparent 75%)'
          }}
        ></div>
        {/* Eyebrow heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 mb-2 sm:mb-4"
        >
          <span className="text-blue-400 font-semibold text-xs sm:text-sm md:text-base uppercase tracking-wide">
            <span className="hidden md:inline">Where Intelligence Meets Efficiency</span>
            <span className="md:hidden">Where Intelligence Meets Efficiency</span>
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative z-10 text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter font-extrabold text-white leading-tight mb-2 sm:mb-4"
        >
          <span className="text-blue-400">SMARTER</span><br />
          <span className="text-blue-400">FASTER</span><br />
          <span className="hidden md:inline">SECURE ENTERPRISE-GRADE AI</span>
          <span className="md:hidden">SECURE ENTERPRISE-GRADE AI</span>
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative z-10 mt-2 sm:mt-6 max-w-3xl mx-auto text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200"
        >
          <span className="hidden md:inline">
            Stop hunting through endless files. Ufficient unifies documents, workflows, and teams into one secure hub for construction, engineering, and legal professionals.
          </span>
          <span className="md:hidden">
            Unify your documents, workflows, and teams in one secure AI-powered platform.
          </span>
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5, type: 'spring', stiffness: 100 }}
          className="relative z-10 mt-4 sm:mt-8 md:mt-10 max-w-2xl mx-auto sm:flex sm:justify-center sm:space-x-4 lg:mt-12"
        >
          <div className="rounded-md shadow-lg mb-4 sm:mb-0">
            <Link
              to="/contact"
              className="w-full inline-flex items-center justify-center px-6 md:px-8 py-3 border border-transparent text-base font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 md:py-4 md:text-lg md:px-10 transition duration-300 ease-in-out transform hover:-translate-y-1"
            >
              <span className="md:hidden">Get Demo</span>
              <span className="hidden md:inline">Start Free Demo</span>
              <ChevronDown size={20} className="ml-2 -rotate-90" />
            </Link>
          </div>
          <div className="rounded-md shadow-lg">
            <Link
              to="/how-it-works"
              className="w-full inline-flex items-center justify-center px-6 md:px-8 py-3 border border-blue-400 text-base font-semibold rounded-md text-blue-400 bg-white bg-opacity-90 hover:bg-opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 md:py-4 md:text-lg md:px-10 transition duration-300 ease-in-out transform hover:-translate-y-1"
            >
              <span className="md:hidden">See How It Works</span>
              <span className="hidden md:inline">See How It Works</span>
              <ChevronRight size={20} className="ml-2" />
            </Link>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-4 sm:bottom-8 left-0 right-0 flex justify-center cursor-pointer group z-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
        onClick={scrollToContent}
      >
        <ChevronDown size={32} className="text-blue-400 transition-transform duration-300 group-hover:translate-y-1 animate-bounce" />
      </motion.div>
    </div>
  );
};

export default UfficientLandingHero;